import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  DirectionsCar,
  Event as EventIcon,
  LinkOff,
  Person,
  Search,
  Storefront,
  SwapHoriz,
} from '@mui/icons-material'
import API from '../../../services/API'
import BigModal from '../../../components/Modals/BigModal'

const TYPES = [
  { key: 'employee', label: 'Colleagues', icon: Person, tone: '#2563EB' },
  { key: 'vehicle', label: 'Vehicles', icon: DirectionsCar, tone: '#D97706' },
  { key: 'event', label: 'Events', icon: EventIcon, tone: '#7C3AED' },
  { key: 'store', label: 'Stores', icon: Storefront, tone: '#1E7F88' },
]
const typeConfig = (key) => TYPES.find((type) => type.key === key)

// Where the bag is right now, for the header chip.
const currentAssignment = (location) => {
  const employee = location?.assignedToEmployee?.userAccount
  if (employee) {
    return {
      type: 'employee',
      label: [employee.firstName, employee.lastName].filter(Boolean).join(' '),
    }
  }
  if (location?.assignedToVehicle) {
    return { type: 'vehicle', label: location.assignedToVehicle.callSign }
  }
  if (location?.assignedToEvent) {
    return { type: 'event', label: location.assignedToEvent.title }
  }
  if (location?.assignedToStore) {
    return { type: 'store', label: location.assignedToStore.name }
  }
  return null
}

// Replaces the old "Move bag" select that loaded every vehicle and store.
// Searches the server (debounced, like EmployeeSelector) across colleagues,
// vehicles, events and stores, scoped to the base locations the caller is
// permitted to see.
const ReassignBag = ({ open, onClose, location, loadPageAPI }) => {
  const [query, setQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [results, setResults] = useState([])
  const [scoped, setScoped] = useState(false)
  const [scopeBases, setScopeBases] = useState([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [confirmUnassign, setConfirmUnassign] = useState(false)
  const requestId = useRef(0)

  useEffect(() => {
    if (!open) return
    setQuery('')
    setTypeFilter('all')
    setResults([])
    setSelected(null)
    setError(null)
    setConfirmUnassign(false)
    // An empty query still tells us which bases the search is limited to,
    // so the warning can show before the first keystroke.
    API.get('/medsmx/reassign-search', { params: { q: '' } })
      .then((res) => {
        if (res.status === 200) {
          setScoped(!!res.data?.scoped)
          setScopeBases(res.data?.bases ?? [])
        }
      })
      .catch(() => {})
  }, [open])

  useEffect(() => {
    const term = query.trim()
    if (term.length < 2) {
      setResults([])
      setSearching(false)
      return undefined
    }
    setSearching(true)
    const id = ++requestId.current
    const timeout = setTimeout(async () => {
      try {
        const res = await API.get('/medsmx/reassign-search', {
          params: {
            q: term,
            types: typeFilter === 'all' ? undefined : typeFilter,
          },
        })
        if (id !== requestId.current) return
        if (res.status === 200) {
          setResults(res.data?.results ?? [])
          setScoped(!!res.data?.scoped)
          setScopeBases(res.data?.bases ?? [])
        } else {
          setResults([])
        }
      } catch (e) {
        if (id === requestId.current) setResults([])
      } finally {
        if (id === requestId.current) setSearching(false)
      }
    }, 350)
    return () => clearTimeout(timeout)
  }, [query, typeFilter])

  const grouped = useMemo(
    () =>
      TYPES.map((type) => ({
        ...type,
        items: results.filter((item) => item.type === type.key),
      })).filter((group) => group.items.length > 0),
    [results],
  )

  const current = currentAssignment(location)
  const alreadyThere =
    selected &&
    current &&
    selected.type === current.type &&
    String(selected._id) ===
      String(
        location?.[
          {
            employee: 'assignedToEmployee',
            vehicle: 'assignedToVehicle',
            event: 'assignedToEvent',
            store: 'assignedToStore',
          }[selected.type]
        ]?._id,
      )

  const handleSubmit = async () => {
    if (!selected) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await API.post('/medsmx/newlocation', {
        id: location._id,
        [selected.type]: selected._id,
      })
      if (res.status === 200) {
        onClose()
        loadPageAPI()
      } else {
        setError(res.data?.error || 'Unable to reassign this bag')
      }
    } catch (e) {
      console.error(e)
      setError('Unable to reassign this bag. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUnassign = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await API.post('/medsmx/newlocation', {
        id: location._id,
        unassign: true,
      })
      if (res.status === 200) {
        onClose()
        loadPageAPI()
      } else {
        setError(res.data?.error || 'Unable to unassign this bag')
      }
    } catch (e) {
      console.error(e)
      setError('Unable to unassign this bag. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const term = query.trim()

  return (
    <BigModal autoWidth open={open} onClose={onClose}>
      <Stack
        gap={1.5}
        sx={{ width: { xs: '100%', sm: 520 }, maxWidth: '90vw' }}
      >
        <Stack gap={0.5}>
          <Typography variant="title2" fontSize="1.2rem">
            Reassign {location?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hand this bag to a colleague, put it on a vehicle, send it to an
            event, return it to a store, or take it off its current assignment.
          </Typography>
          {current && (
            <Chip
              size="small"
              icon={React.createElement(typeConfig(current.type).icon)}
              label={`Currently: ${current.label}`}
              sx={{ width: 'fit-content', fontWeight: 800, mt: 0.5 }}
            />
          )}
        </Stack>

        {scoped && (
          <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
            You will only see colleagues, vehicles, events and stores at the
            base locations you have selected and are permitted to access
            {scopeBases.length > 0
              ? `: ${scopeBases.map((base) => base.name).join(', ')}`
              : ''}
            . Change the base locations selected in the header to see others.
          </Alert>
        )}

        <TextField
          autoFocus
          size="small"
          placeholder="Search by name, call sign, event or store"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
        />

        <ToggleButtonGroup
          exclusive
          size="small"
          value={typeFilter}
          onChange={(e, value) => value && setTypeFilter(value)}
          sx={{
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': { textTransform: 'none', px: 1.25 },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          {TYPES.map((type) => (
            <ToggleButton key={type.key} value={type.key}>
              {type.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box
          sx={{
            border: '1px solid rgba(15,23,42,0.1)',
            borderRadius: 1.5,
            maxHeight: 340,
            overflowY: 'auto',
          }}
        >
          {term.length < 2 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ p: 3 }}
            >
              Type at least two characters to search.
            </Typography>
          ) : !searching && results.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ p: 3 }}
            >
              No matches for &ldquo;{term}&rdquo;.
            </Typography>
          ) : (
            <List dense disablePadding>
              {grouped.map((group) => (
                <li key={group.key}>
                  <ul style={{ padding: 0 }}>
                    <ListSubheader
                      disableSticky
                      sx={{
                        lineHeight: '28px',
                        fontWeight: 900,
                        color: group.tone,
                        backgroundColor: `${group.tone}0D`,
                      }}
                    >
                      {group.label}
                    </ListSubheader>
                    {group.items.map((item) => {
                      const isSelected =
                        selected?.type === item.type &&
                        String(selected?._id) === String(item._id)
                      const Icon = group.icon
                      return (
                        <ListItemButton
                          key={`${item.type}-${item._id}`}
                          selected={isSelected}
                          onClick={() => setSelected(item)}
                          sx={{ gap: 1.25 }}
                        >
                          {item.type === 'employee' ? (
                            <Avatar
                              src={item.photograph}
                              sx={{ width: 28, height: 28, borderRadius: 1 }}
                            >
                              {item.label?.[0]}
                            </Avatar>
                          ) : (
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1,
                                display: 'grid',
                                placeItems: 'center',
                                color: group.tone,
                                backgroundColor: `${group.tone}14`,
                                flexShrink: 0,
                              }}
                            >
                              <Icon fontSize="small" />
                            </Box>
                          )}
                          <ListItemText
                            primary={item.label}
                            secondary={item.sub}
                            primaryTypographyProps={{
                              fontWeight: 800,
                              noWrap: true,
                              color: item.outOfService
                                ? 'error.main'
                                : 'text.primary',
                            }}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                          {item.outOfService && (
                            <Chip
                              size="small"
                              color="error"
                              label="Out of service"
                            />
                          )}
                        </ListItemButton>
                      )
                    })}
                  </ul>
                </li>
              ))}
            </List>
          )}
        </Box>

        {alreadyThere && (
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            This bag is already assigned there.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {confirmUnassign && current && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ alignItems: 'center' }}
            action={
              <Stack direction="row" gap={0.5}>
                <Button
                  size="small"
                  onClick={() => setConfirmUnassign(false)}
                  disabled={submitting}
                >
                  Keep
                </Button>
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  startIcon={<LinkOff />}
                  onClick={handleUnassign}
                  disabled={submitting}
                >
                  Unassign bag
                </Button>
              </Stack>
            }
          >
            Take this bag off {current.label}? It will show as unassigned until
            someone picks it up.
          </Alert>
        )}

        <Stack
          direction="row"
          gap={1}
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <Box>
            {current && !confirmUnassign && (
              <Button
                color="warning"
                startIcon={<LinkOff />}
                onClick={() => setConfirmUnassign(true)}
                disabled={submitting}
              >
                Unassign
              </Button>
            )}
          </Box>
          <Stack direction="row" gap={1}>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SwapHoriz />}
              onClick={handleSubmit}
              disabled={!selected || alreadyThere || submitting}
            >
              {selected
                ? `Reassign to ${selected.label}`
                : 'Select where it goes'}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BigModal>
  )
}

export default ReassignBag
